import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/scripts/AuthContext";
import { useOrgSocialActor } from "@/scripts/OrgSocialActorContext";
import { useSocialPaths } from "@/lib/socialPaths";
import { API_BASE_URL } from "@/lib/apiBase";
import { toastError, toastSuccess } from "@/lib/toast";
import { pathWithPostAnchor } from "@/lib/permalinks";
import { uploadOrgSocialPostMedia, setOrgSocialEmbed } from "@/lib/socialMedia";
import { seedLikedFromPosts } from "@/lib/socialFeedLikes";
import PostComposer from "../PostComposer";
import PostCard from "../PostCard";

function normalizePost(p) {
  if (!p) return p;
  const id = p.social_post_id ?? p.post_id ?? p.postId;
  return {
    ...p,
    post_id: id,
    social_post_id: id,
    title:
      p.repost_kind === "quote"
        ? null
        : p.title ?? null,
    created_at: p.created_at || p.createdAt,
    deleted_at: p.deleted_at || p.deletedAt || null,
    like_count: p.like_count ?? p.likeCount ?? 0,
    reply_count: p.reply_count ?? p.replyCount ?? 0,
    repost_count: p.repost_count ?? p.repostCount ?? 0,
    viewer_has_reposted: Boolean(p.viewer_has_reposted),
    viewer_has_liked: Boolean(p.viewer_has_liked),
  };
}

export default function OrgSocialHomePage() {
  const { accessToken } = useAuth();
  const { orgId, canPublish, displayName } = useOrgSocialActor();
  const paths = useSocialPaths();
  const navigate = useNavigate();
  const location = useLocation();

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [composerFormKey, setComposerFormKey] = useState(0);
  const [likedPosts, setLikedPosts] = useState(() => new Set());

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${accessToken}` }),
    [accessToken]
  );

  const loadHome = useCallback(async () => {
    if (!accessToken || !orgId || !canPublish) {
      setPosts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/org/${orgId}/social/feed/home`,
        { headers }
      );
      const normalized = (res.data?.posts || []).map(normalizePost);
      setPosts(normalized);
      setLikedPosts(seedLikedFromPosts(normalized));
    } catch (err) {
      console.error(err);
      setPosts([]);
      setLikedPosts(new Set());
    } finally {
      setLoading(false);
    }
  }, [accessToken, orgId, canPublish, headers]);

  useEffect(() => {
    loadHome();
  }, [loadHome]);

  const handleCompose = async (payload) => {
    if (!canPublish || posting) return;
    setPosting(true);
    try {
      const { data: created } = await axios.post(
        `${API_BASE_URL}/org/${orgId}/social/posts`,
        {
          title: payload?.title,
          content: payload?.content || payload?.body || "",
          community_id: null,
        },
        { headers }
      );
      const createdPostId =
        created?.social_post_id ?? created?.post_id ?? created?.postId;
      if (createdPostId != null && payload?.files?.length) {
        try {
          for (const file of payload.files) {
            await uploadOrgSocialPostMedia(
              accessToken,
              orgId,
              createdPostId,
              file
            );
          }
        } catch (mediaErr) {
          toastError(mediaErr, "Posted, but media upload failed");
        }
      }
      if (createdPostId != null && payload?.embedUrl) {
        try {
          await setOrgSocialEmbed(
            accessToken,
            orgId,
            createdPostId,
            payload.embedUrl
          );
        } catch (embedErr) {
          toastError(embedErr, "Posted, but video link failed");
        }
      }
      toastSuccess("Posted to Circa Social profile");
      setComposerFormKey((k) => k + 1);
      await loadHome();
    } catch (err) {
      toastError(err, "Could not post");
    } finally {
      setPosting(false);
    }
  };

  const handleLike = async (postId) => {
    if (!canPublish || !postId) return;
    try {
      await axios.post(
        `${API_BASE_URL}/org/${orgId}/social/posts/${postId}/interactions`,
        { interaction_type: "like" },
        { headers }
      );
      setLikedPosts((prev) => new Set(prev).add(String(postId)));
      setPosts((prev) =>
        prev.map((p) => {
          const id = String(p.post_id ?? p.social_post_id);
          if (id !== String(postId)) return p;
          return { ...p, like_count: (p.like_count ?? 0) + 1 };
        })
      );
    } catch (err) {
      toastError(err, "Could not like");
    }
  };

  const openThread = (post) => {
    const postId = post?.post_id ?? post?.social_post_id;
    if (postId == null) return;
    navigate(
      paths.thread({
        postId,
        returnPath: pathWithPostAnchor(
          `${location.pathname}${location.search || ""}`,
          postId
        ),
      })
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl min-w-0 px-4 py-6 space-y-6">
      <div className="hidden min-w-0 md:block">
        <h2 className="text-lg font-semibold">Home</h2>
        <p className="text-sm text-muted-foreground mt-1 break-words">
          Posts from accounts {displayName} follows.
        </p>
      </div>

      {canPublish ? (
        <PostComposer
          mode="collapsed"
          destinationMode="fixed"
          hideDestination
          hideTags
          inferTitle={false}
          fixedTeamId="org-profile"
          fixedTeamLabel={`${displayName}'s followers`}
          isSubmitting={posting}
          onSubmit={handleCompose}
          formKey={composerFormKey}
          allowCritical={false}
          submitLabel="Post"
        />
      ) : null}

      {posts.length === 0 ? (
        <p className="text-center text-sm text-muted-foreground py-8">
          {canPublish
            ? "Your org feed is empty. Follow people from Explore to see posts here."
            : "Follow and engage require Social publish permission."}
        </p>
      ) : (
        posts.map((p) => {
          const id = p.post_id || p.social_post_id;
          return (
            <PostCard
              key={id}
              post={p}
              authorAboveTitle
              socialHome
              relativeTimestamps
              socialTombstone
              socialRepost
              accessToken={accessToken}
              isLiked={likedPosts.has(String(id))}
              onLike={canPublish ? () => handleLike(id) : undefined}
              onExpand={() => openThread(p)}
            />
          );
        })
      )}
    </div>
  );
}
